import { useCallback, useRef } from 'react';
import {
  keepSelectedData,
  selectionChangeNeedsConfirm
} from './selectionGuard';

/**
 * ChartBox's half of the unsaved-edits guard on a change of selection. The
 * sidebar's half, which asks and discards, is useDiscardGuard. The rule they
 * share is in ./selectionGuard.js.
 *
 * A hook rather than inline in ChartBox so the whole round trip (dirty sidebar,
 * click, prompt, keep or discard) can be exercised in a test: nothing in the
 * repo can mount ChartBox itself.
 *
 * @param {object}   args
 * @param {Function} [args.onUnsavedSectionsChange] where the sidebar's dirty
 *        section labels go next (the tab strip, TGCLOUD-1839)
 * @param {{current: Set}}    args.selectedIdsRef  the committed selection
 * @param {{current: object}} args.dataRef         the committed `data`
 * @param {Function} args.setData
 * @returns {{
 *   unsavedSectionsRef: {current: string[]},
 *   discardGuardRef: {current: Function|null},
 *   handleUnsavedSectionsChange: Function,
 *   guardSelection: Function,
 *   bindSelectedData: Function
 * }}
 */
export default function useSelectionGuard({
  onUnsavedSectionsChange,
  selectedIdsRef,
  dataRef,
  setData
}) {
  // The sidebar's unsaved section labels, mirrored on their way to the tab
  // strip. A ref, because the canvas binds its click handlers once and they
  // have to read the value at click time, not at bind time.
  const unsavedSectionsRef = useRef([]);
  const handleUnsavedSectionsChange = useCallback(
    (sections) => {
      unsavedSectionsRef.current = Array.isArray(sections) ? sections : [];
      onUnsavedSectionsChange?.(sections);
    },
    [onUnsavedSectionsChange]
  );

  // Filled by the sidebar with its confirmDiscard (useDiscardGuard).
  const discardGuardRef = useRef(null);

  /**
   * Run `apply`, the selection change, unless it would re-bind a dirty sidebar
   * to a different record. Then the sidebar asks, and runs `apply` only after
   * "Discard and switch" (or `confirmLabel`) has discarded the edits.
   *
   * `next` describes the selection `apply` makes: { selectedCount, dataPid }.
   * Stable, and reads everything through refs, so d3 handlers bound long ago
   * still see the current state.
   */
  const guardSelection = useCallback(
    (apply, { next, confirmLabel } = {}) => {
      const confirm = discardGuardRef.current;
      const current = {
        selectedCount: selectedIdsRef.current?.size ?? 0,
        dataPid: dataRef.current?.pid ?? null
      };
      if (
        !confirm ||
        !selectionChangeNeedsConfirm({
          unsavedSections: unsavedSectionsRef.current,
          current,
          next
        })
      ) {
        apply();
        return;
      }
      confirm(apply, { confirmLabel });
    },
    [selectedIdsRef, dataRef]
  );

  // Set `data`, but keep the existing object when the same person is
  // re-selected while anything is unsaved, so the click does not reset the
  // form it landed on. See keepSelectedData.
  const bindSelectedData = useCallback(
    (next) =>
      setData((prev) =>
        keepSelectedData(prev, next, unsavedSectionsRef.current) ? prev : next
      ),
    [setData]
  );

  return {
    unsavedSectionsRef,
    discardGuardRef,
    handleUnsavedSectionsChange,
    guardSelection,
    bindSelectedData
  };
}
